import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiEndpointService } from './api-endpoint.service';
import { ToastAlertService } from 'src/app/shared/utils/toast.service';
import { GlobalService } from './global.service';
import {
  IOcrBeneficiaryDetails,
  IOcrClaimSubmissionResult,
  IOcrEligibility,
  IOcrSelfDeclarationTemplate,
  IOcrSubmitClaimPayload,
  IOcrVerifySubmitClaimPayload,
} from '../domain/operational-cost-reimbursement.model';

/**
 * BRD §22 (2026-09-15, real-paperwork correction) — shared by both
 * `submitClaim()` and `verifySubmitClaim()` so the full Beneficiary
 * Details field set is appended identically in both places, rather than
 * two hand-maintained lists that could drift.
 */
function appendBeneficiaryDetails(
  formData: FormData,
  payload: IOcrBeneficiaryDetails,
): void {
  formData.append('companyName', payload.companyName);
  formData.append('companyAddress', payload.companyAddress);
  formData.append('panNumber', payload.panNumber);
  formData.append('bankName', payload.bankName);
  formData.append('bankBranchName', payload.bankBranchName);
  formData.append('bankAccountType', payload.bankAccountType);
  formData.append('micrCode', payload.micrCode);
  formData.append('bankAccountNumber', payload.bankAccountNumber);
  formData.append('bankIfscCode', payload.bankIfscCode);
  formData.append('beneficiaryEmail', payload.beneficiaryEmail);
  formData.append('beneficiaryMobileNumber', payload.beneficiaryMobileNumber);
}

/**
 * SAN-756/757 — Operational Cost Reimbursement (T-RISE, Slices 1-2).
 * Consumes `sc-saas-backend`'s `operational-cost-reimbursement` module.
 */
@Injectable({
  providedIn: 'root',
})
export class OperationalCostReimbursementService {
  constructor(
    private http: HttpClient,
    private toastService: ToastAlertService,
    private globalService: GlobalService,
  ) {}

  public getEligibility(): Observable<IOcrEligibility> {
    const url = ApiEndpointService.getEndpoint(
      ApiEndpointService.ENDPOINT.OCR_ELIGIBILITY,
    );
    return this.http.get(url).pipe(
      map((response: any): IOcrEligibility => response?.data),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(
          this.globalService.getErrorMessage(
            fault,
            'Error while fetching eligibility',
          ),
          'error',
        );
        return throwError(() => fault);
      }),
    );
  }

  public getSelfDeclarationTemplate(): Observable<IOcrSelfDeclarationTemplate> {
    const url = ApiEndpointService.getEndpoint(
      ApiEndpointService.ENDPOINT.OCR_SELF_DECLARATION_TEMPLATE,
    );
    return this.http.get(url).pipe(
      map((response: any): IOcrSelfDeclarationTemplate => response?.data),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(
          this.globalService.getErrorMessage(
            fault,
            'Error while fetching the self-declaration template',
          ),
          'error',
        );
        return throwError(() => fault);
      }),
    );
  }

  public submitClaim(
    payload: IOcrSubmitClaimPayload,
  ): Observable<IOcrClaimSubmissionResult> {
    const formData = new FormData();
    formData.append('category', payload.category);
    appendBeneficiaryDetails(formData, payload);
    // BRD §22 — ONE combined self-declaration/shareholding-pattern upload;
    // `shareholding` is no longer a separate field at all.
    // SAN-965/SAN-971 — either may be omitted when already on file; the
    // backend carries the existing one forward.
    if (payload.selfDeclaration) {
      formData.append('selfDeclaration', payload.selfDeclaration);
    }
    if (payload.bankChequeOrPassbook) {
      formData.append('bankChequeOrPassbook', payload.bankChequeOrPassbook);
    }
    if (payload.disabilityCertificate) {
      formData.append('disabilityCertificate', payload.disabilityCertificate);
    }

    const url = ApiEndpointService.getEndpoint(
      ApiEndpointService.ENDPOINT.OCR_CLAIMS,
    );
    return this.http.post(url, formData).pipe(
      map((response: any): IOcrClaimSubmissionResult => response?.data),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(
          this.globalService.getErrorMessage(
            fault,
            'Error while submitting your claim',
          ),
          'error',
        );
        return throwError(() => fault);
      }),
    );
  }

  /**
   * BRD FR-5.1 (Slice 4, BRD §21) — Verify & Submit, Month 2 onward.
   * Bank-details-only body (category/documents are carried forward
   * server-side, never re-sent here) — mirrors `jobs.service.ts`'s
   * `ENDPOINT.JOBS + \`${id}/details\`` pattern for a dynamic path segment,
   * since `OCR_CLAIMS` (unlike `JOBS`) has no trailing slash.
   */
  public verifySubmitClaim(
    monthIndex: number,
    payload: IOcrVerifySubmitClaimPayload,
  ): Observable<IOcrClaimSubmissionResult> {
    const formData = new FormData();
    appendBeneficiaryDetails(formData, payload);
    if (payload.bankChequeOrPassbook) {
      formData.append('bankChequeOrPassbook', payload.bankChequeOrPassbook);
    }
    // SAN-908 — only sent when this is the startup's first-ever submission
    // (no prior claim to carry a self-declaration forward from).
    if (payload.selfDeclaration) {
      formData.append('selfDeclaration', payload.selfDeclaration);
    }

    const url = ApiEndpointService.getEndpoint(
      ApiEndpointService.ENDPOINT.OCR_CLAIMS + `/${monthIndex}/verify-submit`,
    );
    return this.http.post(url, formData).pipe(
      map((response: any): IOcrClaimSubmissionResult => response?.data),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(
          this.globalService.getErrorMessage(
            fault,
            'Error while submitting your claim',
          ),
          'error',
        );
        return throwError(() => fault);
      }),
    );
  }
}
